import React, { useState } from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { getPhotoForUserId } from "../../utils/utils";
import DeviceOrientation, { Orientation } from "react-screen-orientation";
import attachment from "../../images/attachment.png";
import add_spec from "../../images/Add_Spec_Sheet_Icon.png";
import { Spin, Button as AntButton } from "antd";
import Spinner from "../ui/Spinner";
import PhotoSelect from "../ui/PhotoSelect";
import ConfirmDelete from "../ui/ConfirmDelete";
import { Link } from "react-router-dom";
import {
  InfoSquareFill,
  ChevronUp,
  ChevronDown,
  ExclamationTriangle,
  Plus,
  Paperclip,
} from "react-bootstrap-icons";
import Modal from "react-bootstrap/Modal";
import { SpecsheetImage } from "../specsheetImage/specsheetImage";
import { SpecsheetManage } from "../specsheetImage/specsheetManage";
import { SpecsheetAuditModal } from "./specsheetAuditModal";
import ScanHandoff from "./ScanHandoff";

const SpecsheetFormNoteLazy = React.lazy(() =>
  import("./specsheetFormNote.jsx")
);

const StyledGalleryModal = styled(Modal)`
  .modal-content {
    background-color: #ffffff;
    box-shadow: inset 0 4px 0 ${(p) => p.theme.color.primary},
      0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    border-radius: ${(p) => p.theme.radius.md};
    border: none;
  }
  .modal-header {
    border-bottom: 1px solid ${(p) => p.theme.color.border};
    padding: calc(${(p) => p.theme.spacing.md} + 4px)
      ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.md};
  }
  .modal-body {
    padding: ${(p) => p.theme.spacing.md};
    min-height: 320px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f6fa;
  }
  .modal-footer {
    border-top: 1px solid ${(p) => p.theme.color.border};
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  }
`;

const GalleryModalTitle = styled.span`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeLg};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  color: ${(p) => p.theme.color.primary};
  line-height: 1.2;
`;

const GalleryMedia = styled.div`
  width: 100%;
  max-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;

  & img,
  & video {
    max-width: 100%;
    max-height: 70vh;
    width: auto;
    height: auto;
    display: block;
    border-radius: ${(p) => p.theme.radius.md};
    background: #ffffff;
  }
`;

const GalleryEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 280px;
  color: ${(p) => p.theme.color.textMuted};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};

  svg {
    width: 32px;
    height: 32px;
    color: #c3c7d4;
  }
`;

const BorderContainer = styled.div`
  width: 100%;
  width: -webkit-fill-available;
  height: 80vh;
  margin-top: 1vh;
  background: var(--unnamed-color-ffffff) 0% 0% no-repeat padding-box;
  border: 1px solid var(--unnamed-color-d4d4d5);
  background: #ffffff 0% 0% no-repeat padding-box;
  border: 1px solid #d4d4d5;
  border-radius: 4px;
  opacity: 1;
  display: flex;
  align-content: center;
  justify-content: center;
  align-items: center;
`;

const SpecsheetRoot = styled.div`
  position: relative;
  width: 100%;

  @media only screen and (min-width: 992px) and (orientation: landscape) {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }
`;

const SvgNotesRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;

  @media only screen and (min-width: 992px) and (orientation: landscape) {
    position: relative;
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
  }
`;

const SvgDrawerSplit = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;

  @media only screen and (min-width: 992px) and (orientation: landscape) {
    flex: 1 1 0;
    min-height: 0;
    flex-direction: ${(props) => (props.expanded ? "row" : "column")};
    gap: ${(props) => (props.expanded ? "12px" : "0")};
    align-items: stretch;

    > * {
      min-width: 0;
      min-height: 0;
    }
  }
`;

const PortraitNotesBar = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: stretch;
  gap: 6px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  flex: 0 0 auto;

  &:hover {
    background: #f9fafb;
  }

  @media (max-width: 991px) {
    margin-top: 10px;
  }
`;

const PortraitNotesBarTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

const PortraitNotesBarTopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
`;

const PortraitNotesBarTitle = styled.span`
  color: #00187e;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  line-height: 1.2;
  flex: 1 1 auto;
  min-width: 0;
`;

const PortraitNotesBarRow = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e7eaf6;
  color: #00187e;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  border: 1px solid #b8c2dd;
  line-height: 1.3;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;

  ${(props) =>
    props.$fullWidth &&
    `
    align-self: stretch;
    width: 100%;
    justify-content: flex-start;
  `}
`;

const PortraitNotesBarChevron = styled.span`
  color: #00187e;
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const RailToggle = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #00187e;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;

  &:hover {
    background: rgba(0, 24, 126, 0.08);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ExpandedDrawer = styled.div`
  flex: 1 1 100%;
  width: 100%;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 12px 14px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-sizing: border-box;

  @media (max-width: 991px) {
    margin-top: 12px;
  }

  @media only screen and (min-width: 992px) and (orientation: landscape) {
    flex: 0 0 40%;
    max-width: 40%;
    height: 100%;
    align-self: stretch;
    margin: 0;
  }
`;

const ExpandedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 1px solid #eef0f5;
  padding-bottom: 8px;
  margin-bottom: 4px;
`;

const ExpandedTitle = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #00187e;
  text-transform: uppercase;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  background: ${(p) => (p.muted ? "#f3f4f6" : "#e6f8fa")};
  color: ${(p) => (p.muted ? "#9ca3af" : "#00187e")};
  font-size: 11px;
  font-weight: 600;
  border: 1px solid ${(p) => (p.muted ? "#e5e7eb" : "#00becc")};
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

const Button = styled.button`
  background: #00becc 0% 0% no-repeat padding-box;
  border-color: #00becc;
  border-radius: 4px;
  margin-top: 7px;
  opacity: 1;
  width: 272px;
  height: 48px;
  font-weight: 500;

  &:hover {
    background: #00187e;
    border-color: #00187e;
    color: #ffffff;
  }
`;

const FormContent = styled.div`
  padding: 0.4em;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 0.4em;
  min-height: 0;

  @media only screen and (min-width: 992px) and (orientation: landscape) {
    flex: 1 1 0;
    height: 100%;
    overflow: hidden;
  }

  @media only screen and (max-width: 991px) {
    overflow-y: auto;
    -webkit-overflow-scroll: touch;
  }

  & form {
    font-size: 10px;
  }

  & .ant-row.ant-form-item {
    margin: 0;

    .ant-form-item-label label {
      font-size: 1.2em;
      color: #6c757e;
      text-transform: capitalize;
    }

    .ant-input {
      border-color: black;
    }

    .ant-select {
      min-width: 100% !important;

      .ant-select-selection {
        border-color: black;
      }

      .ant-select-arrow {
        color: rgba(0, 0, 0, 0.5);
      }
    }
  }
`;

const NotesSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;

  @media only screen and (min-width: 992px) and (orientation: landscape) {
    flex: 1 1 50%;
    min-height: 0;
    overflow: hidden;

    & > div {
      height: 100%;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .ql-container,
    .quill {
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .ql-editor {
      flex: 1 1 auto;
      min-height: 120px;
      overflow-y: auto;
    }
  }
`;

const AttachmentsSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  border-top: 1px solid #eef0f5;
  padding-top: 8px;

  @media only screen and (min-width: 992px) and (orientation: landscape) {
    flex: 1 1 50%;
    min-height: 0;
    overflow: hidden;
  }
`;

const AttachmentsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
`;

const AttachmentsTitle = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #00187e;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 13px;
    height: 13px;
  }
`;

const AttachmentsAddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #00187e;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #001463;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const AttachmentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
  width: 100%;
  align-content: start;

  @media only screen and (min-width: 992px) and (orientation: landscape) {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-right: 4px;
  }
`;

const AttachmentEmptyCard = styled.button`
  border: 1.5px dashed #c3c7d4;
  border-radius: 10px;
  background: #f5f6fa;
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #6b7280;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  padding: 8px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: #ffffff;
    border-color: #00187e;
    color: #00187e;
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;
const ContainerLogos = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: auto;
  justify-content: space-between;
  color: #00197b;
  font-weight: 700;
  font: normal normal bold 12px/13px Poppins;
  margin-left: 15px;
  margin-right: 15px;
  margin-top: 14px;
  margin-bottom: 7px;
  z-index: 888;

  @media only screen and (max-width: 991px) and (orientation: portrait) {
    flex-direction: row;
    justify-content: space-evenly;
  }
`;
const ContainerRows = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-evenly;
  color: rgb(0, 25, 123);
  font-weight: 900;
  cursor: default;

  ${(props) =>
    props.margenException &&
    css`
      @media only screen and (max-width: 991px) {
        margin-top: auto;
      }
    `}

  @media only screen and (max-width: 991px) and (orientation: portrait) {
    flex-direction: row;
    justify-content: space-evenly;
  }
`;

const logoStyle = {
  width: "17px",
  height: "16px",
  padding: "0",
  margin: "0 5px 0 0",
  cursosor: "pointer",
  fontSize: "12px",
};

const carouselContainerStyle = {
  overflowX: "scroll",
  overflowY: "hidden",
  scrollbarColor: "rgba(0, 0, 0, .5) rgba(0, 0, 0, 0)",
  whiteSpace: "nowrap",
};

export const Specsheet = ({
  specsheets,
  specsheetAddNew,
  specsheetClone,
  customerPrint,
  specsheetSVGClick,
  isRightHander,
  currentSpecsheet,
  userId,
  specsheetSetSpecsheet,
  specsheetSavePhoto,
  specsheetScanPhoto,
  specsheetScanPhotoAI,
  specsheetDeletePhoto,
  specSheetRemove,
  balls,
  bowlerID,
  isReadOnly,
  specSheetGetAuditRecords,
  pluginSubscriptions,
  savingPhotoAI,
  specSheetSendEmail,
  setShowSharingModal,
  userEmail,
  rightColumn,
  notesAtBottom,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [srcToShow, setSrcToShow] = useState("");
  const [showPhotoModal, setPhotoShowModal] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [imageIndexSelected, selectImageIndex] = useState(-1);
  const [galleryPreviewError, setGalleryPreviewError] = useState(false);

  React.useEffect(() => {
    setGalleryPreviewError(false);
  }, [srcToShow, showModal]);
  const initialNoteText = currentSpecsheet?.note
    ? currentSpecsheet.note.replace(/<[^>]*>/g, "").trim()
    : "";
  const initialHasNotes =
    initialNoteText !== "" ||
    (currentSpecsheet?.gallery && currentSpecsheet.gallery.length > 0);
  const [notesCollapsed, setNotesCollapsed] = useState(!initialHasNotes);
  const [isLandscapeDesktop, setIsLandscapeDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(
          "(min-width: 992px) and (orientation: landscape)"
        ).matches
      : false
  );

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(
      "(min-width: 992px) and (orientation: landscape)"
    );
    const handler = (e) => setIsLandscapeDesktop(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  const { t } = useTranslation();
  const isSSR = typeof window === "undefined";
  const getPhoto = getPhotoForUserId(userId);
  const srcToCheck = srcToShow?.toLowerCase().split("?")[0] || "";
  const isVideo =
    srcToCheck.includes("data:video") ||
    ["mp4", "mov"].includes(srcToCheck.split(".").pop());
  const handleCreateSpecsheet = () => {
    specsheetAddNew({});
  };

  const GalleryModal = (props) => {
    const { src } = props;
    const srcToCheck = src?.toLowerCase().split("?")[0] || "";
    const isImage =
      srcToCheck.includes("data:image") ||
      ["png", "jpg", "jpeg"].includes(srcToCheck.split(".").pop());
    const isVideo =
      srcToCheck.includes("data:video") ||
      ["mp4", "mov"].includes(srcToCheck.split(".").pop());

    const showError = !src || galleryPreviewError || (!isImage && !isVideo);

    return (
      <StyledGalleryModal
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        dialogClassName="gallery-popup"
        centered
      >
        <Modal.Header closeButton>
          <GalleryModalTitle>
            {isVideo ? t("Video Preview") : t("Image Preview")}
          </GalleryModalTitle>
        </Modal.Header>
        <Modal.Body>
          {showError ? (
            <GalleryEmpty>
              <ExclamationTriangle />
              <span>
                {!src
                  ? t("No media available")
                  : t("Could not load this file")}
              </span>
            </GalleryEmpty>
          ) : (
            <GalleryMedia>
              {isImage && (
                <img
                  src={src}
                  alt=""
                  onError={() => setGalleryPreviewError(true)}
                />
              )}
              {isVideo && (
                <video
                  src={src}
                  autoPlay
                  controls
                  onError={() => setGalleryPreviewError(true)}
                />
              )}
            </GalleryMedia>
          )}
        </Modal.Body>
        <Modal.Footer>
          {isImage && (
            <Link
              to={`/print/${bowlerID}/specsheet/${currentSpecsheet.id}/attachment/${imageIndexSelected}`}
              target="_blank"
            >
              <AntButton
                style={{
                  background: "none",
                  fontSize: 12,
                  border: "none",
                  margin: 0,
                  color: "#00197B",
                  boxShadow: "none",
                }}
              >
                {t("Print")}
              </AntButton>
            </Link>
          )}
          <AntButton
            onClick={() => {
              setShowConfirmDelete(true);
            }}
            style={{
              background: "none",
              fontSize: 12,
              border: "none",
              margin: 0,
              color: "#F0142F",
              boxShadow: "none",
            }}
          >
            {" "}
            {isVideo
              ? t("photoSelectModal.deleteVideo")
              : t("photoSelectModal.delete")}
          </AntButton>
        </Modal.Footer>
      </StyledGalleryModal>
    );
  };

  const InfoButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-evenly;
    position: absolute;
    bottom: 0;
    right: 10px;
  `;

  const InfoButtonButton = styled.button`
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #00197b;
    font-size: 24px;
    font-weight: 600;
    background: transparent;
    border: none;
  `;
  const InfoButton = () => (
    <InfoButtonContainer>
      <InfoButtonButton
        onClick={() => {
          specSheetGetAuditRecords({ id: currentSpecsheet.id });
        }}
      >
        <InfoSquareFill />
      </InfoButtonButton>
    </InfoButtonContainer>
  );

  const GalleryCard = ({ src, onClick }) => {
    const srcToCheck = src?.toLowerCase().split("?")[0] || "";
    const isImage =
      srcToCheck.includes("data:image") ||
      ["png", "jpg", "jpeg"].includes(srcToCheck.split(".").pop());
    const isVideo =
      srcToCheck.includes("data:video") ||
      ["mp4", "mov"].includes(srcToCheck.split(".").pop());

    return (
      <div
        style={{
          height: 120,
          width: "100%",
          borderWidth: 1,
          borderRadius: 10,
          borderStyle: "solid",
          borderColor: "#e5e7eb",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#ffffff",
          cursor: "pointer",
        }}
        onClick={onClick}
      >
        {isImage && (
          <img
            src={src}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        )}
        {isVideo && (
          <video
            src={src}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        )}
      </div>
    );
  };

  return (
    <>
      {specsheets.length >= 1 ? (
        <SpecsheetRoot>
          <SpecsheetManage
            specsheets={specsheets}
            specsheetAddNew={specsheetAddNew}
            specsheetClone={specsheetClone}
            customerPrint={customerPrint}
            specsheetSVGClick={specsheetSVGClick}
            isRightHander={isRightHander}
            specsheetSetSpecsheet={specsheetSetSpecsheet}
            currentSpecsheet={currentSpecsheet}
            specSheetRemove={specSheetRemove}
            isReadOnly={isReadOnly}
            balls={balls}
            bowlerID={bowlerID}
            specsheetScanPhoto={specsheetScanPhoto}
            specsheetScanPhotoAI={specsheetScanPhotoAI}
            pluginSubscriptions={pluginSubscriptions}
            savingPhotoAI={savingPhotoAI}
            specSheetSendEmail={specSheetSendEmail}
            userEmail={userEmail}
            setShowSharingModal={setShowSharingModal}
            specSheetGetAuditRecords={specSheetGetAuditRecords}
          />
          {(() => {
            const galleryItems = currentSpecsheet?.gallery || [];
            const notesPanel = (
              <FormContent>
                <NotesSection>
                  <div className="col-12 col-lg-12">
                    {!isSSR && (
                      <React.Suspense fallback={<Spinner />}>
                        <SpecsheetFormNoteLazy />
                      </React.Suspense>
                    )}
                  </div>
                </NotesSection>
                <AttachmentsSection>
                  <AttachmentsHeader>
                    <AttachmentsTitle>
                      <Paperclip />
                      {t("form.label.attachments")}
                    </AttachmentsTitle>
                  </AttachmentsHeader>
                  <AttachmentsGrid>
                    {galleryItems.map((imageSrc, index) => (
                      <GalleryCard
                        key={index}
                        src={getPhoto(`${imageSrc}?ttl=${Date().toString()}`)}
                        onClick={() => {
                          setSrcToShow(
                            getPhoto(`${imageSrc}?ttl=${Date().toString()}`)
                          );
                          selectImageIndex(index);
                          setShowModal(true);
                        }}
                      />
                    ))}
                    {!isReadOnly && (
                      <AttachmentEmptyCard
                        type="button"
                        onClick={() => setPhotoShowModal(true)}
                        title={t("form.label.add")}
                      >
                        <Plus />
                        <span>{t("form.label.add")}</span>
                      </AttachmentEmptyCard>
                    )}
                  </AttachmentsGrid>
                </AttachmentsSection>
              </FormContent>
            );
            const noteText = currentSpecsheet?.note
              ? currentSpecsheet.note.replace(/<[^>]*>/g, "").trim()
              : "";
            const attachmentCount = currentSpecsheet?.gallery?.length || 0;
            const expanded = isLandscapeDesktop ? true : !notesCollapsed;
            const attachmentsLabel =
              attachmentCount > 0
                ? `${attachmentCount} ${
                    attachmentCount === 1 ? "attachment" : "attachments"
                  }`
                : "No attachments";
            const notesStatusLabel = noteText || "No notes";
            const bar = notesAtBottom && !isLandscapeDesktop && (
              <PortraitNotesBar
                type="button"
                onClick={() => setNotesCollapsed((c) => !c)}
                aria-expanded={expanded}
                aria-label={
                  expanded
                    ? "Collapse notes & attachments"
                    : "Expand notes & attachments"
                }
              >
                <PortraitNotesBarTopRow>
                  <PortraitNotesBarTitle>
                    Notes & Attachments
                  </PortraitNotesBarTitle>
                  <PortraitNotesBarTopRight>
                    <PortraitNotesBarRow>
                      {attachmentsLabel}
                    </PortraitNotesBarRow>
                    <PortraitNotesBarChevron>
                      {expanded ? <ChevronUp /> : <ChevronDown />}
                    </PortraitNotesBarChevron>
                  </PortraitNotesBarTopRight>
                </PortraitNotesBarTopRow>
                <PortraitNotesBarRow $fullWidth>
                  {notesStatusLabel}
                </PortraitNotesBarRow>
              </PortraitNotesBar>
            );
            const drawer = notesAtBottom && expanded && (
              <ExpandedDrawer>{notesPanel}</ExpandedDrawer>
            );
            return (
              <SvgNotesRow expanded={expanded}>
                <SvgDrawerSplit expanded={expanded}>
                  <SpecsheetImage />
                  {isLandscapeDesktop && drawer}
                </SvgDrawerSplit>
                {!notesAtBottom && notesPanel}
                {bar}
                {!isLandscapeDesktop && drawer}
                {rightColumn}
              </SvgNotesRow>
            );
          })()}
          <GalleryModal
            show={showModal}
            onHide={() => {
              setSrcToShow(getPhoto(""));
              setShowModal(false);
            }}
            src={srcToShow}
          />
          <PhotoSelect
            circle={false}
            show={showPhotoModal}
            showVideo={true}
            onHide={() => {
              selectImageIndex(-1);
              setPhotoShowModal(false);
            }}
            onFromCamera={(imageBase64, extension) => {
              specsheetSavePhoto({
                imageBase64,
                extension: extension || "png",
              });
              setPhotoShowModal(false);
            }}
            onFromGallery={(imageBase64, extension) => {
              specsheetSavePhoto({
                imageBase64,
                extension: extension || "png",
              });
              setPhotoShowModal(false);
            }}
            onFromVideo={(video, extension) => {
              // Fetch the Blob from the Blob URL
              fetch(video)
                .then((response) => response.blob())
                .then((blob) => {
                  // Convert the Blob to Base64
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    const base64data = reader.result.split(";base64,"); // This is the Base64 string
                    specsheetSavePhoto({
                      imageBase64: base64data[1], // Assign the Base64 string to imageBase64
                      extension: extension || "mp4",
                    });
                    setPhotoShowModal(false);
                  };
                  reader.readAsDataURL(blob); // Read the Blob as a Base64 string
                })
                .catch((error) => {
                  console.error(
                    "Error converting video Blob to Base64:",
                    error
                  );
                  setPhotoShowModal(false);
                });
            }}
          />
          <ConfirmDelete
            show={showConfirmDelete}
            onHide={() => {
              setShowConfirmDelete(false);
            }}
            confirmButtonText={
              isVideo
                ? "message.confirm.deleteVideo"
                : "message.confirm.deleteImage"
            }
            confirmDescription={
              isVideo
                ? "message.confirm.confirmDeleteVideo"
                : "message.confirm.confirmDeleteImage"
            }
            onConfirmClick={() => {
              specsheetDeletePhoto({ deleteIndex: imageIndexSelected });
              setShowConfirmDelete(false);
              setSrcToShow(getPhoto(""));
              setShowModal(false);
              selectImageIndex(-1);
            }}
          />
          <SpecsheetAuditModal />
          <ScanHandoff bowlerID={bowlerID} />
        </SpecsheetRoot>
      ) : (
        <BorderContainer>
          {!isReadOnly && (
            <Button className="btn btn-primary" onClick={handleCreateSpecsheet}>
              {t("form.label.add_spec_sheets")}
            </Button>
          )}
        </BorderContainer>
      )}
    </>
  );
};
